import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Box, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, IconButton, Tooltip, Alert, Dialog, DialogTitle, DialogContent,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import api from "../api";
import { MONO, tokens } from "../theme";
import { PageHeader, Metric, Eyebrow } from "../components/ui";
import ModelRegistryModal from "../components/ModelRegistryModal";

const STATUS_COLOR = { mirrored: tokens.ok, mirroring: tokens.signal, error: tokens.crit, registered: tokens.slate };

export default function ModelRegistry() {
  const [rows, setRows] = useState(null);
  const [storage, setStorage] = useState(null);
  const [totals, setTotals] = useState(null);
  const [modal, setModal] = useState(null);      // {model} | {create:true} | null
  const [logFor, setLogFor] = useState(null);
  const [logText, setLogText] = useState("");

  const load = useCallback(() => {
    api.get("/api/model-registry", { silent: true })
      .then((d) => { setRows(d.models); setStorage(d.storage); setTotals(d.totals); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while anything is downloading.
  const busy = (rows || []).some((m) => m.status === "mirroring");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [busy, load]);

  // Poll the open log.
  const logRef = useRef(null);
  useEffect(() => {
    if (!logFor) return;
    const fetchLog = () => api.get(`/api/model-registry/${logFor}/log`, { silent: true })
      .then((d) => setLogText(d.log || "")).catch(() => {});
    fetchLog();
    const t = setInterval(fetchLog, 2000);
    return () => clearInterval(t);
  }, [logFor]);
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [logText]);

  const mirror = async (name) => {
    try { await api.post(`/api/model-registry/${name}/mirror`, {}); setLogText(""); setLogFor(name); load(); }
    catch { /* toasts */ }
  };
  const refresh = async (name) => {
    try { await api.post(`/api/model-registry/${name}/refresh`, {}); toast.success("Metadata refreshed"); load(); }
    catch { /* toasts */ }
  };
  const del = async (name) => {
    if (!window.confirm(`Delete model "${name}"? (also removes its mirrored objects)`)) return;
    try { await api.del(`/api/model-registry/${name}`); toast.success("Deleted"); load(); } catch { /* toasts */ }
  };

  if (!rows) return null;
  const cats = Object.entries(totals?.by_category || {}).sort((a, b) => b[1].count - a[1].count);

  return (
    <Box>
      <PageHeader
        eyebrow="Serve"
        title="Model Registry"
        subtitle="Mirror HuggingFace models into S3-compatible storage, then serve them from the mirror (mirror://<name>) instead of public HuggingFace — for vLLM models and zero-shot classifiers."
        actions={<Button variant="contained" size="small" onClick={() => setModal({ create: true })}>Register model</Button>}
      />

      {storage && !storage.configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No object storage configured — set <code>GEN_S3_BUCKET</code> (+ endpoint / keys) to enable mirroring.
          The registry is browsable meanwhile.
        </Alert>
      )}

      {/* Widgets: total space + counts + per-category breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 2 }}>
            <Metric label="Storage used" value={totals?.size_human?.split(" ")[0] || "0"}
              unit={totals?.size_human?.split(" ")[1] || "B"} accent={tokens.signal} size="lg" />
            <Metric label="Models" value={totals?.counts?.total ?? 0} />
            <Metric label="Mirrored" value={totals?.counts?.mirrored ?? 0} accent={tokens.ok} />
            <Metric label="Errors" value={totals?.counts?.error ?? 0}
              accent={totals?.counts?.error ? tokens.crit : undefined} />
          </Box>
          {cats.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Eyebrow sx={{ mb: 1 }}>By category</Eyebrow>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {cats.map(([cat, c]) => (
                  <Chip key={cat} size="small" variant="outlined"
                    label={`${cat} · ${c.count}${c.size_bytes ? ` · ${fmtBytes(c.size_bytes)}` : ""}`} />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Precision</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.name}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600 }}>{m.name}</span>
                      {m.source === "custom" && <Chip size="small" variant="outlined" label="custom" />}
                    </Box>
                    <Tooltip title="Reference this from a vLLM model or classifier's model id">
                      <Box sx={{ fontFamily: MONO, fontSize: ".68rem", color: "text.secondary" }}>{m.mirror_ref}</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontFamily: MONO, fontSize: ".76rem", color: "text.secondary" }}>{m.model_id}</TableCell>
                  <TableCell>{m.category ? <Chip size="small" label={m.category} /> : "—"}</TableCell>
                  <TableCell sx={{ fontFamily: MONO, fontSize: ".76rem" }}>
                    {m.precision || "—"}{m.quantization ? ` / ${m.quantization}` : ""}
                    {m.params ? <Box sx={{ color: "text.secondary", fontSize: ".7rem" }}>{m.params}</Box> : null}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: MONO, fontSize: ".76rem" }}>{m.size_human}</TableCell>
                  <TableCell>
                    <Chip size="small" label={m.status}
                      sx={{ bgcolor: `${STATUS_COLOR[m.status] || tokens.slate}22`,
                            color: STATUS_COLOR[m.status] || tokens.slate, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={m.status === "mirrored" ? "Re-mirror from HuggingFace" : "Download from HuggingFace → S3"}>
                      <span><IconButton size="small" onClick={() => mirror(m.name)}
                        disabled={!storage?.configured || m.status === "mirroring"}>
                        <CloudDownloadIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <Tooltip title="Refresh metadata from HuggingFace">
                      <IconButton size="small" onClick={() => refresh(m.name)}><RefreshIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Mirror log">
                      <IconButton size="small" onClick={() => { setLogText(""); setLogFor(m.name); }}>
                        <ArticleIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setModal({ model: m })}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {m.source === "custom" && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => del(m.name)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {modal && (
        <ModelRegistryModal model={modal.model || null}
          onClose={(changed) => { setModal(null); if (changed) load(); }} />
      )}

      <Dialog open={!!logFor} onClose={() => setLogFor(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: MONO, fontSize: ".9rem" }}>{logFor} — mirror log</DialogTitle>
        <DialogContent dividers>
          <Box ref={logRef} component="pre" sx={{ fontFamily: MONO, fontSize: ".72rem", m: 0, maxHeight: 420,
            overflow: "auto", whiteSpace: "pre-wrap" }}>{logText || "(no log yet)"}</Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${i === 0 ? n : n.toFixed(1)} ${u[i]}`;
}
